import { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useLang } from '../lib/lang';
import { IMAGES } from '../content/constants';
import { Page, LineReveal, Reveal, VoltButton, Overline } from '../components/motion';
import { MarqueeStrip, SkeletonGrid, CTASection, LinkTile, PageSection, SectionHead } from '../components/blocks';

export default function Home() {
  const { t, link } = useLang();
  const c = t.home;
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  const imgY = useTransform(scrollYProgress, [0, 1], ['0%', '22%']);
  const fade = useTransform(scrollYProgress, [0, 0.85], [1, 0.2]);

  return (
    <Page meta={{ ...t.meta.home, lang: t.lang }}>
      <section ref={heroRef} data-testid="home-hero" className="relative min-h-[100svh] flex flex-col overflow-hidden blueprint">
        <motion.div style={{ y: imgY }} className="absolute inset-0" aria-hidden="true">
          <img src={IMAGES.heroHome} alt="" className="w-full h-[115%] object-cover grayscale brightness-[0.42] contrast-110" />
          <div className="absolute inset-0 bg-gradient-to-b from-obsidian/80 via-obsidian/30 to-obsidian" />
        </motion.div>
        <div className="absolute inset-0 spotlight" aria-hidden="true" />
        <motion.div style={{ opacity: fade }} className="relative flex-1 flex flex-col justify-center w-full max-w-7xl mx-auto px-6 md:px-12 pt-36 pb-16">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 0.1 }}>
            <Overline>{c.hero.overline}</Overline>
          </motion.div>
          <LineReveal
            lines={c.hero.lines}
            className="mt-8 font-display font-black uppercase tracking-tighter leading-[0.93] text-white text-[clamp(2.8rem,8.5vw,7.5rem)]"
          />
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="mt-8 max-w-2xl text-base md:text-lg text-zinc-400 leading-relaxed"
          >
            {c.hero.sub}
          </motion.p>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.85 }}
            className="mt-4 font-mono text-[11px] uppercase tracking-[0.2em] text-volt"
          >
            {c.hero.cert}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.95, ease: [0.22, 1, 0.36, 1] }}
            className="mt-10 flex flex-wrap gap-4"
          >
            <VoltButton to={link('contact')} testId="hero-cta-primary">{c.hero.btnPrimary}</VoltButton>
            <VoltButton to={link('power')} variant="outline" testId="hero-cta-secondary">{c.hero.btnSecondary}</VoltButton>
          </motion.div>
        </motion.div>
        <div data-testid="trust-bar" className="relative border-t border-white/10 bg-obsidian/70 backdrop-blur-md">
          <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4">
            {c.trust.map((s, i) => (
              <div key={i} className={`px-6 md:px-10 py-6 md:py-8 ${i > 0 ? 'border-l border-white/10' : ''} ${i > 1 ? 'max-md:border-t max-md:border-white/10' : ''} ${i === 2 ? 'max-md:border-l-0' : ''}`}>
                <p className="font-display text-2xl md:text-4xl font-black tracking-tight text-white">{s.v}</p>
                <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500 leading-relaxed">{s.l}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <MarqueeStrip items={c.marquee} />

      <PageSection overline={c.deliver.overline} title={c.deliver.title} testId="deliver-section">
        <div className="grid md:grid-cols-3 border-t border-l border-white/10">
          {c.deliver.items.map((item) => (
            <LinkTile
              key={item.key}
              to={link(item.key)}
              title={item.title}
              body={item.body}
              linkLabel={item.link}
              testId={`deliver-tile-${item.key}`}
            />
          ))}
        </div>
      </PageSection>

      <section data-testid="manifesto-section" className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-36">
          <div className="grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <SectionHead overline={c.manifesto.overline} title={c.manifesto.title} />
            </div>
            <div className="lg:col-span-6 lg:col-start-7">
              <Reveal delay={0.1}>
                <p className="text-base text-zinc-400 leading-relaxed">{c.manifesto.p1}</p>
                <p className="mt-6 text-base text-white leading-relaxed border-l-2 border-volt pl-6">{c.manifesto.p2}</p>
              </Reveal>
            </div>
          </div>
          <div className="mt-16 md:mt-24">
            <SkeletonGrid items={c.manifesto.steps} numbered cols="md:grid-cols-2 lg:grid-cols-3" testId="manifesto-steps" />
          </div>
          <Reveal className="mt-16 md:mt-24 max-w-4xl">
            <p className="font-display text-2xl md:text-4xl font-bold tracking-tight leading-tight text-white">
              <span className="text-volt">///</span> {c.manifesto.closing}
            </p>
          </Reveal>
        </div>
      </section>

      <PageSection overline={c.why.overline} title={c.why.title} testId="why-section" className="bg-surface/40">
        <SkeletonGrid items={c.why.items} cols="md:grid-cols-2 lg:grid-cols-4" testId="why-grid" />
      </PageSection>

      <CTASection title={c.cta.title} body={c.cta.body} btn={c.cta.btn} />
    </Page>
  );
}
